import React, { useState } from 'react';
import { Modal, Typography, Rate, Input, Button, message } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import { THEME_COLORS, COLORS, GRADIENTS } from '../../utils/colors';

const { Title, Text } = Typography;
const { TextArea } = Input;
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

const FeedbackModal = ({ open, onCancel, onSend }) => {
  const { getToken, user } = useAuth();
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    try {
      setLoading(true);
      const token = await getToken({ template: 'default' });

      const response = await fetch(`${BASE_URL}/feedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rating,
          feedback: feedback.trim(),
          user_id: user?.id,
          email: user?.primaryEmailAddress?.emailAddress,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        message.success('Thank you for the Feedback', 3);
        // Mark as completed in localStorage
        localStorage.setItem('hooptie_feedback_completed', 'true');
        localStorage.setItem('hooptie_feedback_date', new Date().toISOString());
        onSend();
      } else {
        const errorData = await response.json().catch(() => ({}));
        message.error(errorData.message || 'Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      message.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMaybeLater = () => {
    // Store that user dismissed it, but allow it to show again after some time
    localStorage.setItem('hooptie_feedback_dismissed', new Date().toISOString());
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleMaybeLater}
      footer={null}
      closable={false}
      width={500}
      style={{ top: '50%', transform: 'translateY(-50%)' }}
      bodyStyle={{
        background: THEME_COLORS.DARK.bg.secondary,
        padding: '32px',
        borderRadius: '16px',
        border: `1px solid ${THEME_COLORS.DARK.border.secondary}`
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <Title level={2} style={{
          color: THEME_COLORS.DARK.text.primary,
          marginBottom: 12,
          fontSize: '24px',
          fontWeight: 700
        }}>
          How is Hooptie working for you?
        </Title>
        <Text style={{
          color: THEME_COLORS.DARK.text.secondary,
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          Your feedback helps us make the app better for independent dealers like you.
        </Text>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{
          color: THEME_COLORS.DARK.text.primary,
          fontSize: '16px',
          display: 'block',
          marginBottom: 12
        }}>
          Overall experience
        </Text>
        <Rate
          value={rating}
          onChange={setRating}
          style={{ fontSize: 32 }}
          allowClear={false}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{
          color: THEME_COLORS.DARK.text.primary,
          fontSize: '16px',
          display: 'block',
          marginBottom: 12
        }}>
          Anything we can improve?
        </Text>
        <TextArea
          rows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Write your feedback here..."
          style={{
            background: THEME_COLORS.DARK.bg.tertiary,
            border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
            color: THEME_COLORS.DARK.text.primary,
            resize: 'vertical'
          }}
        />
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12
      }}>
        <Button
          onClick={handleMaybeLater}
          style={{
            background: 'transparent',
            border: `1px solid ${THEME_COLORS.DARK.border.primary}`,
            color: THEME_COLORS.DARK.text.primary,
            borderRadius: '8px',
            height: '40px',
            padding: '0 24px'
          }}
        >
          Maybe later
        </Button>
        <Button
          type="primary"
          onClick={handleSend}
          loading={loading}
          style={{
            background: COLORS.PRIMARY[500],
            border: 'none',
            borderRadius: '8px',
            height: '40px',
            padding: '0 24px',
            fontWeight: 600
          }}
        >
          Send
        </Button>
      </div>
    </Modal>
  );
};

export default FeedbackModal;

